import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Flag as FlagIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import api from '../api';

function ConversationsList() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [flaggedFilter, setFlaggedFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');

  useEffect(() => {
    fetchConversations();
    fetchStats();
  }, [page, rowsPerPage, searchTerm, statusFilter, flaggedFilter]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (flaggedFilter) params.append('flagged', flaggedFilter);

      const response = await api.get(`/wally-conversations?${params}`);
      setConversations(response.data.conversations);
      setTotalCount(response.data.pagination.total);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/wally-conversations/stats/overview');
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleFlaggedFilterChange = (event) => {
    setFlaggedFilter(event.target.value);
    setPage(0);
  };

  const handleViewConversation = (conversationId) => {
    navigate(`/conversations/${conversationId}`);
  };

  const handleCopyToSlack = async (conversation) => {
    try {
      const messages = Array.isArray(conversation.messages) ? conversation.messages : [];
      const userName = conversation.userName || 'Unknown User';
      const userEmail = conversation.userEmail || 'No email';
      
      // Format conversation for Slack
      let slackText = `🤖 *Wally AI Conversation*\n`;
      slackText += `👤 *User:* ${userName} (${userEmail})\n`;
      slackText += `🆔 *User ID:* ${conversation.userId}\n`;
      slackText += `📅 *Date:* ${formatTimestamp(conversation.createdAt)}\n`;
      slackText += `📊 *Status:* ${conversation.status}\n`;
      if (conversation.flagged) {
        slackText += `🚩 *FLAGGED FOR REVIEW*\n`;
      }
      slackText += `💬 *Messages:* ${messages.length}\n\n`;
      
      slackText += `*Conversation:*\n`;
      slackText += `\`\`\`\n`;
      
      messages.forEach((message, index) => {
        const role = message.role === 'user' ? '👤 User' : '🤖 Wally';
        const timestamp = message.timestamp ? ` (${new Date(message.timestamp).toLocaleTimeString()})` : '';
        const decodedContent = decodeHtmlEntities(message.content);
        slackText += `${role}${timestamp}:\n${decodedContent}\n\n`;
      });
      
      slackText += `\`\`\`\n`;
      slackText += `🔗 *View Details:* ${window.location.origin}/conversations/${conversation.id}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(slackText);
      setCopySuccess('Conversation copied to clipboard! Ready to paste in Slack.');
      
      // Clear success message after 3 seconds
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('Failed to copy conversation');
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      case 'readyToSendToX':
        return 'warning';
      case 'sentToX':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    
    // Create a temporary element to decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const getMessagePreview = (messages) => {
    if (!Array.isArray(messages) || messages.length === 0) {
      return 'No messages';
    }
    
    const lastMessage = messages[messages.length - 1];
    const rawContent = lastMessage.content || 'No content';
    const decodedContent = decodeHtmlEntities(rawContent);
    const preview = decodedContent.substring(0, 100);
    return preview.length > 100 ? `${preview}...` : preview;
  };

  const getMessageCount = (messages) => {
    return Array.isArray(messages) ? messages.length : 0;
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" component="h1">
          Wally AI Conversations
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchConversations();
            fetchStats();
          }}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics */}
      {stats && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Overview
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Box>
              <Typography variant="h4" color="primary">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Conversations
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="success.main">
                {stats.active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="info.main">
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="warning.main">
                {stats.flagged}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Flagged
              </Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="secondary.main">
                {stats.uniqueUsers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique Users
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="readyToSendToX">Ready to Send</MenuItem>
              <MenuItem value="sentToX">Sent to X</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Flagged</InputLabel>
            <Select
              value={flaggedFilter}
              onChange={handleFlaggedFilterChange}
              label="Flagged"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Flagged</MenuItem>
              <MenuItem value="false">Not Flagged</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Messages</TableCell>
              <TableCell>Last Message Preview</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Alert severity="error">{error}</Alert>
                </TableCell>
              </TableRow>
            ) : conversations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No conversations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              conversations.map((conversation) => (
                <TableRow key={conversation.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {conversation.id.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {conversation.userName || 'Unknown User'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {conversation.userId.substring(0, 8)}...
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={conversation.status}
                        color={getStatusColor(conversation.status)}
                        size="small"
                      />
                      {conversation.flagged && (
                        <Chip
                          icon={<FlagIcon />}
                          label="Flagged"
                          color="warning"
                          size="small"
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getMessageCount(conversation.messages)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {getMessagePreview(conversation.messages)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimestamp(conversation.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimestamp(conversation.updatedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View conversation">
                        <IconButton
                          onClick={() => handleViewConversation(conversation.id)}
                          size="small"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy to Slack">
                        <IconButton
                          onClick={() => handleCopyToSlack(conversation)}
                          size="small"
                          color="primary"
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Copy Success Message */}
      {copySuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            right: 20, 
            zIndex: 1000,
            minWidth: 300
          }}
        >
          {copySuccess}
        </Alert>
      )}
    </Box>
  );
}

export default ConversationsList;
